import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { campaignApi, engagementApi } from '../services/api';
import { Campaign, CampaignEngagement, CampaignSendDetail, CampaignStats } from '../types';
import {
  SEND_PILL, STAGE_LABEL, STAGE_ORDER, errorLabel, fmtDay, fmtDayTime, fmtSentRead, fmtTime, sendIssueText, stageCls,
} from '../lib';
import StatStrip from '../components/StatStrip';
import EngagementFunnelStrip from '../components/EngagementFunnelStrip';
import EngagementTable from '../components/EngagementTable';
import StageOutcomeBars from '../components/StageOutcomeBars';
import { Section, Dash, Empty, Failed, LeadCell, Loading, PageFrame, StatusChip, Strip, Tabs, Th, btn, btnTone, tr } from '../components/PlatformBits';
import { ReplyCell, VisitCell, useReplies } from '../components/SendCells';

type Segment = '' | 'clicked' | 'replied' | 'visited' | 'failed' | 'skipped';

// One predicate per tab — the table filter and the tab's count both read it.
const IN_SEGMENT: Record<Segment, (s: CampaignSendDetail) => boolean> = {
  '': () => true,
  clicked: (s) => !!s.clicked_at,
  replied: (s) => !!s.replied_at,
  visited: (s) => !!s.visited_at,
  failed: (s) => s.status === 'failed',
  skipped: (s) => s.status === 'skipped',
};

const CampaignDetailPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [sends, setSends] = useState<CampaignSendDetail[] | null>(null);
  const [engagement, setEngagement] = useState<CampaignEngagement | null>(null);
  const [segment, setSegment] = useState<Segment>('');
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!campaignId) return;
    try {
      const [c, s, sd, eng] = await Promise.all([
        campaignApi.get(campaignId),
        campaignApi.stats(campaignId),
        campaignApi.sends(campaignId),
        engagementApi.forCampaign(campaignId),
      ]);
      setCampaign(c);
      setStats(s);
      setSends(sd.data);
      setEngagement(eng);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const replies = useReplies(sends);

  const transition = async (verb: 'pause' | 'resume' | 'abort') => {
    if (!campaign) return;
    if (verb === 'abort' && !window.confirm(`Abort "${campaign.name}"? This is terminal.`)) return;
    try {
      await campaignApi.transition(campaign.id, verb);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || `Failed to ${verb} campaign`);
    }
  };

  const visible = useMemo(() => {
    if (!sends) return [];
    const q = query.toLowerCase().trim();
    return sends.filter((s) => {
      const segOk = IN_SEGMENT[segment](s);
      const stOk = !stage || s.lead_stage === stage;
      const qOk = !q ||
        s.lead_name.toLowerCase().includes(q) ||
        (s.lead_phone || '').replace(/\s/g, '').includes(q.replace(/\s/g, ''));
      return segOk && stOk && qOk;
    });
  }, [sends, segment, stage, query]);

  const exportCsv = () => {
    const header = ['Name', 'Phone', 'Stage', 'Attempt', 'Status', 'Sent', 'Read', 'Replied', 'Clicked', 'Visited', 'Visit status', 'Error'];
    const rows = visible.map((s) => [
      s.lead_name, s.lead_phone || '', s.lead_stage || '', s.attempt_n, s.status,
      s.sent_at || '', s.read_at || '', s.replied_at || '', s.clicked_at || '',
      s.visited_at || '', s.visit_task_status || '',
      s.error_code ? errorLabel(s.error_code) : (s.error_detail || ''),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${campaign?.name || 'campaign'}-leads.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return <Failed what="campaign" error={error} />;
  if (!campaign || !stats || !sends) return <div className="px-10 pt-8"><Loading what="campaign" /></div>;

  const retries = campaign.retry_policy?.delays_hours || [];
  const ordinal = (n: string) => (n === '1' ? '1st' : n === '2' ? '2nd' : n === '3' ? '3rd' : `${n}th`);
  const attemptSplit = Object.keys(stats.attempts).length > 1
    ? Object.entries(stats.attempts)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([n, count]) => `${count} on the ${ordinal(n)} attempt`)
      .join(' · ')
    : '';
  const errorSplit = Object.entries(stats.error_codes)
    .map(([code, n]) => `${errorLabel(Number(code))} ×${n}`)
    .join(', ');

  // Tab counts are the send rows that tab shows, not the per-lead stats above.
  const rowsIn = (seg: Segment) => sends.filter(IN_SEGMENT[seg]).length;
  const tab = (seg: Segment, label: string): [Segment, React.ReactNode] =>
    [seg, <>{label} <span className="num opacity-70">{rowsIn(seg)}</span></>];
  const segments: [Segment, React.ReactNode][] = [
    tab('', 'Everyone'),
    tab('clicked', 'Clicked'),
    tab('replied', 'Replied'),
    tab('visited', 'Site visit'),
    ...(rowsIn('failed') > 0 ? [tab('failed', 'Failed')] : []),
    ...(rowsIn('skipped') > 0 ? [tab('skipped', 'Skipped')] : []),
  ];
  const filtered = segment !== '' || stage !== '' || query.trim() !== '';
  // Stage chips: the stages present in this campaign's send rows, ladder order.
  const stageCounts = sends.reduce<Record<string, number>>((acc, s) => {
    if (s.lead_stage) acc[s.lead_stage] = (acc[s.lead_stage] || 0) + 1;
    return acc;
  }, {});
  const stagesPresent = [...STAGE_ORDER, ...Object.keys(stageCounts).filter((st) => !STAGE_ORDER.includes(st))].filter((st) => stageCounts[st]);
  const live = ['Draft', 'Active', 'Paused'].includes(campaign.status);

  const inputCls = 'h-8 px-3 w-[200px] rounded-lg border border-charcoal/15 bg-warm-white text-[13px] placeholder:text-graphite/70 focus:outline-none focus:border-forest focus:ring-2 focus:ring-sage/40';

  return (
    <PageFrame
      title={campaign.name}
      back={{ to: '/whatsapp', label: 'All campaigns' }}
      chip={<StatusChip status={campaign.status} />}
      subline={<>
        {campaign.description && <span className="text-charcoal">{campaign.description} · </span>}
        <span className="num">started {fmtDay(campaign.created_at)}</span>
        {retries.length > 0 && <> · retries after {retries.map((h) => `${h}h`).join(', ')}</>}
      </>}
      action={live && <div className="flex items-center gap-2">
        {campaign.status === 'Active' && <button onClick={() => transition('pause')} className={`${btn} ${btnTone.charcoal}`}>Pause sending</button>}
        {campaign.status === 'Paused' && <button onClick={() => transition('resume')} className={`${btn} ${btnTone.forest}`}>Resume sending</button>}
        {campaign.status !== 'Draft' && <span aria-hidden className="w-px h-5 bg-charcoal/15 mx-1" />}
        <button onClick={() => transition('abort')} title="Stops the campaign for good — it cannot be resumed"
                className={`${btn} ${btnTone.brick}`}>Abort…</button>
      </div>}
    >
      <Strip>
        <StatStrip stats={stats} showDelivered />
        {(stats.failed > 0 || stats.skipped > 0 || attemptSplit) && (
          <ul className="mt-4 space-y-1 text-[12.5px] text-graphite">
            {stats.failed > 0 && (
              <li><span className="font-semibold text-brick">{stats.failed} failed</span>{errorSplit && <> — {errorSplit}</>}</li>
            )}
            {stats.skipped > 0 && (
              <li><span className="font-semibold text-amber-ink">{stats.skipped} skipped</span> before sending</li>
            )}
            {attemptSplit && <li>{attemptSplit}</li>}
          </ul>
        )}
      </Strip>

      {stats.by_stage.length > 0 && (
        <Section title="By stage" note={<span>each lead once, by its best outcome · click a bar to filter the leads below</span>}>
          <StageOutcomeBars rows={stats.by_stage} selected={stage} onSelect={setStage} />
        </Section>
      )}

      <Section title="Leads" note={<span className="num">{filtered ? `${visible.length} of ${sends.length}` : sends.length} send rows</span>}>
        <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-3">
          <span className="inline-flex items-center gap-2 flex-wrap">
            <Tabs value={segment} onChange={setSegment} items={segments} />
            {stagesPresent.length > 1 && (
              <span className="inline-flex flex-wrap gap-1" role="group" aria-label="Filter by stage">
                {stagesPresent.map((st) => (
                  <button key={st} onClick={() => setStage(stage === st ? '' : st)} aria-pressed={stage === st}
                          className={`inline-flex items-baseline gap-1 text-[11px] font-medium px-2 py-px rounded-full border whitespace-nowrap transition-colors ${stageCls(st)} ${stage === st ? 'ring-2 ring-forest/60' : 'opacity-80 hover:opacity-100'}`}>
                    <b className="num font-semibold">{stageCounts[st]}</b>{STAGE_LABEL[st] ?? st}
                  </button>
                ))}
              </span>
            )}
          </span>
          <span className="inline-flex items-center gap-2">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or number"
              aria-label="Search leads by name or number"
              className={inputCls}
            />
            <button onClick={exportCsv} disabled={visible.length === 0} className={`${btn} ${btnTone.charcoal}`}
                    title="Download the rows shown as CSV">Export CSV</button>
          </span>
        </div>
        <table className="w-full min-w-[760px] border-collapse">
          <thead>
            <tr>{['Lead', 'Status', 'Sent → read', 'Clicked', 'Their reply', 'Site visit'].map((h) => <Th key={h} left ours={h === 'Site visit'}>{h}</Th>)}</tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <Empty cols={6}>
                No leads match.{' '}
                {filtered && <button onClick={() => { setSegment(''); setStage(''); setQuery(''); }}
                                     className="ml-1 font-semibold text-forest hover:underline underline-offset-2">Clear filters</button>}
              </Empty>
            )}
            {visible.map((s) => {
              const issue = sendIssueText(s);
              return (
                <tr key={s.id} className={`${tr} hover:bg-charcoal/[0.025]`}>
                  <LeadCell id={s.lead_id} name={s.lead_name}
                            sub={<span className="num">{s.lead_phone || '—'}{s.attempt_n > 1 && <span className="text-amber-ink font-semibold"> · attempt {s.attempt_n}</span>}</span>} />
                  <td className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${SEND_PILL[s.status]}`}>
                      {s.status === 'scheduled' ? 'queued' : s.status}
                    </span>
                    {issue && (
                      <div className={`text-[11.5px] mt-1 whitespace-nowrap ${s.status === 'skipped' ? 'text-amber-ink' : 'text-brick'}`}>
                        {issue}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[12.5px] text-graphite whitespace-nowrap num">
                    {s.status === 'scheduled'
                      ? `fires ${fmtDayTime(s.scheduled_at!)}` // a scheduled row is an open task; due_at is NOT NULL
                      : fmtSentRead(s.sent_at, s.read_at)}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {s.clicked_at
                      ? <span className="text-forest font-semibold text-[13px] num">✓ {fmtTime(s.clicked_at)}</span>
                      : <Dash />}
                  </td>
                  <ReplyCell repliedAt={s.replied_at} rows={s.replied_at ? replies[s.id] : undefined} />
                  <VisitCell visitedAt={s.visited_at} status={s.visit_task_status} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      {/* Link engagement — session-source attributed (legacy links first-touch) */}
      {engagement && engagement.funnel.links > 0 && (
        <Section title="Link engagement" note="people who opened a tracked link from this campaign" pad>
          <div className="px-3 pb-5"><EngagementFunnelStrip funnel={engagement.funnel} /></div>
          <EngagementTable links={engagement.links} />
        </Section>
      )}
    </PageFrame>
  );
};

export default CampaignDetailPage;
